import Api from '../Api';
import checkAddEventPayload from './checkAddEventPayload';
import { buildAllCitiesSelection, isAllCitiesSelection } from './allCitiesOfCountry';
import {
  CITY_MODES,
  CONTACT_FIELDS,
  SELECTION_MODES,
  fetchCitiesOfCountryCached,
  isValidOwnerId,
} from './organizationsBulkAutofill';
import { normalizeContactDigits, stripJsonFromPastedText } from './organizationAutofill';
import {
  isValidHoldingTime,
  parseHoldingDatesListForForm,
} from './formatHoldingDatesList';
import { normalizePhotoUrls } from './photoUrls';

export {
  CITY_MODES,
  CONTACT_FIELDS,
  SELECTION_MODES,
  fetchCitiesOfCountryCached,
  isValidOwnerId,
};

export const EVENT_STATUSES = {
  draft: 'draft',
  creating: 'creating',
  created: 'created',
  error: 'error',
};

export function buildInitialBulkEventConfig() {
  return {
    ownerId: '',
    countryMode: SELECTION_MODES.all,
    country: null,
    cityMode: CITY_MODES.perOffer,
    city: null,
    categoryMode: SELECTION_MODES.all,
    category: null,
    photoUrls: [],
  };
}

export function normalizeBulkEventConfig(config) {
  const country = config.countryMode === SELECTION_MODES.single && config.country?._id ? config.country : null;
  const category = config.categoryMode === SELECTION_MODES.single && config.category?._id ? config.category : null;
  const cityMode = country ? config.cityMode : CITY_MODES.perOffer;
  const city = cityMode === CITY_MODES.single && config.city?._id ? config.city : null;

  return {
    ...config,
    country,
    category,
    cityMode,
    city,
    photoUrls: normalizePhotoUrls(config.photoUrls).slice(0, 15),
  };
}

export function getEventConfigIssues(config) {
  const issues = [];

  if (!isValidOwnerId(config.ownerId)) {
    issues.push('Укажите ID владельца — 24 символа (hex).');
  }
  if (config.countryMode === SELECTION_MODES.single && !config.country?._id) {
    issues.push('Выберите страну или переключите режим на «Разные страны».');
  }
  if (config.categoryMode === SELECTION_MODES.single && !config.category?._id) {
    issues.push('Выберите категорию или переключите режим на «Разные категории».');
  }
  if (config.countryMode === SELECTION_MODES.single
    && config.country?._id
    && config.cityMode === CITY_MODES.single
    && !config.city?._id) {
    issues.push('Выберите город или переключите режим города.');
  }

  return issues;
}

export async function fetchEventCategories() {
  const { data } = await Api.getEventCategories();
  return data?.categories || [];
}

function makeUid() {
  return `event_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function toTrimmedString(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

export function shouldAskModelForEventCities(config) {
  return Boolean(config.country) && config.cityMode === CITY_MODES.perOffer;
}

function forcedCitySelection(config) {
  if (!config.country) return null;
  if (config.cityMode === CITY_MODES.allCitiesOfCountry) {
    return buildAllCitiesSelection(config.country);
  }
  if (config.cityMode === CITY_MODES.single && config.city?._id) {
    return { ...config.city, is_in_all_cities_of_country: false };
  }
  return null;
}

function buildReferenceData(config, references) {
  const { categories, languages, cities } = references;
  const reference = {
    languages: (languages || []).map((lang) => ({
      _id: lang._id,
      name: lang.name,
      code: lang.code || '',
    })),
  };

  if (config.category) {
    reference.fixed_category = { _id: config.category._id, name: config.category.name };
  } else {
    reference.event_categories = (categories || []).map((category) => ({
      _id: category._id,
      name: category.name,
    }));
  }

  if (config.country) {
    reference.fixed_country = { _id: config.country._id, name: config.country.name };
  }

  if (shouldAskModelForEventCities(config)) {
    reference.cities = (cities || []).map((city) => ({
      _id: city._id,
      name: city.name,
      country_id: city.country_id || config.country._id,
    }));
  } else if (config.cityMode === CITY_MODES.single && config.city?._id) {
    reference.fixed_city = { _id: config.city._id, name: config.city.name };
  }

  return reference;
}

function buildModeSummaryLines(config) {
  const lines = [];
  lines.push(`— Страна: ${config.country ? `одна для всех — ${config.country.name}` : 'разные (город оператор выберет в админке)'}`);

  if (!config.country) {
    lines.push('— Город: не указывается в JSON, оператор выберет его в админке для каждого мероприятия');
  } else if (config.cityMode === CITY_MODES.allCitiesOfCountry) {
    lines.push(`— Город: «Все города страны ${config.country.name}» для всех (адрес и координаты не нужны)`);
  } else if (config.cityMode === CITY_MODES.single) {
    lines.push(`— Город: один для всех — ${config.city?.name}`);
  } else {
    lines.push('— Город: у каждого мероприятия свой, из REFERENCE_DATA.cities');
  }

  if (config.category) {
    lines.push(`— Категория: одна для всех — ${config.category.name}`);
  } else {
    lines.push('— Категория: у каждого мероприятия своя, из REFERENCE_DATA.event_categories');
  }

  lines.push(`— ID владельца: ${config.ownerId} (проставляется админкой, в JSON не нужен)`);

  if (config.photoUrls?.length) {
    lines.push(`— Фото: админка проставит всем ${config.photoUrls.length} ссылок; photo_urls в JSON не указывай`);
  } else {
    lines.push('— photo_urls: необязательно. Только реальные http(s)-ссылки из данных. Не выдумывай URL');
  }

  lines.push('— Языки: если language_ids нет, мероприятие будет «для всех языков»');

  return lines;
}

function buildEventShape(config) {
  const shape = {
    name: 'Название мероприятия',
    holding_dates_list: ['2026-06-12', '2026-06-13'],
    holding_time: '19:00',
    description: 'Описание мероприятия',
    contacts: {
      phone_number: '79991234567',
      website: 'https://example.com',
    },
    language_ids: ['<_id из REFERENCE_DATA.languages, либо [] если для всех языков>'],
  };

  if (!config.category) {
    shape.events_category_id = '<_id из REFERENCE_DATA.event_categories>';
  }
  if (shouldAskModelForEventCities(config)) {
    shape.city_id = '<_id из REFERENCE_DATA.cities>';
  }
  if (config.cityMode !== CITY_MODES.allCitiesOfCountry) {
    shape.address = 'Улица, дом';
    shape.coordinates = { lat: 40.177, lon: 44.503 };
  }
  return shape;
}

export function buildBulkEventsFileContent({ config, references }) {
  const referenceData = buildReferenceData(config, references);
  const expectedResponse = { events: [buildEventShape(config)] };

  const prompt = [
    'Ты — помощник администратора Nomad: помогаешь пакетно создавать мероприятия в админке.',
    '',
    '────────── Порядок работы ──────────',
    '1) Это первое сообщение — инструкция, режим и справочники. JSON на него генерировать НЕ надо.',
    '2) Ответь ровно одной фразой: «Задача ясна, присылай данные о мероприятиях».',
    '3) Дальше оператор присылает тексты, таблицы, файлы, ссылки. Каждое сообщение подтверждай: «Принято, мероприятий в накоплении: N». JSON не выводи.',
    '4) Когда оператор напишет «Собери файл», верни один блок ```json … ``` со всеми накопленными мероприятиями в массиве events.',
    '5) Если после сборки пришли ещё данные — накапливай их и по команде верни JSON только с новыми мероприятиями.',
    '',
    '────────── РЕЖИМ РАБОТЫ (задан оператором) ──────────',
    ...buildModeSummaryLines(config),
    '',
    'КРИТИЧНО: зафиксированные оператором значения применяются ко ВСЕМ мероприятиям, даже если в тексте другое. Эти поля в JSON не добавляй.',
    '',
    '────────── Обязательные поля каждого мероприятия ──────────',
    '— name — не короче 2 символов;',
    '— holding_dates_list — массив дат YYYY-MM-DD, минимум одна;',
    '— holding_time — необязательно, одно общее время HH:MM на все дни;',
    '— description — не короче 2 символов;',
    '— contacts — хотя бы одно заполненное поле; phone_number, whatsapp, viber — только цифры;',
    !config.category ? '— events_category_id — только _id из REFERENCE_DATA.event_categories;' : '',
    shouldAskModelForEventCities(config) ? '— city_id — только _id из REFERENCE_DATA.cities;' : '',
    config.cityMode !== CITY_MODES.allCitiesOfCountry ? '— address и coordinates — обязательны, если город не «все города страны»;' : '',
    '— language_ids — массив _id из REFERENCE_DATA.languages. Пустой массив значит «для всех языков»;',
    '— photo_urls — только если в данных есть прямые ссылки на картинки. Не выдумывай их.',
    '',
    '────────── КРИТИЧНО — формат ответа ──────────',
    '— Только блок ```json … ```, без текста до и после.',
    '— Внутри один валидный JSON: { "events": [ ... ] }.',
    '',
    '────────── ОЖИДАЕМЫЙ ФОРМАТ ОТВЕТА (заглушки заменить реальными данными) ──────────',
    JSON.stringify(expectedResponse, null, 2),
    '',
    'REFERENCE_DATA:',
    JSON.stringify(referenceData, null, 2),
  ].filter((line) => line !== undefined).join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — массовое создание мероприятий (данные + промпт для ИИ)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Отправьте модели весь этот файл одним сообщением.',
    '2) Присылайте данные о мероприятиях — сколько угодно сообщений.',
    '3) Напишите «Собери файл». Модель вернёт блок ```json … ``` с массивом events.',
    '4) Загрузите ответ на том же экране админки.',
    '5) Поправьте карточки и создайте выбранные. Фото можно добавить файлом на экране одного мероприятия или ссылкой прямо здесь.',
    '',
    prompt,
  ].join('\n');
}

function contactsFromRaw(raw) {
  const contacts = {};
  if (!raw.contacts || typeof raw.contacts !== 'object') return contacts;
  CONTACT_FIELDS.forEach(({ key, digitsOnly }) => {
    const value = toTrimmedString(raw.contacts[key]);
    if (!value) return;
    contacts[key] = digitsOnly ? normalizeContactDigits(value) : value;
  });
  return contacts;
}

function coordinatesFromRaw(raw) {
  const source = raw.coordinates;
  if (!source || typeof source !== 'object') return null;
  const lat = toTrimmedString(source.lat ?? source.latitude);
  const lon = toTrimmedString(source.lon ?? source.lng ?? source.longitude);
  if (!lat && !lon) return null;
  return {
    lat,
    lon,
    is_special_point_on_map: Boolean(source.is_special_point_on_map),
  };
}

function holdingFromRaw(raw) {
  const list = Array.isArray(raw.holding_dates_list) ? raw.holding_dates_list : [];
  const parsed = parseHoldingDatesListForForm(list);
  const dates = parsed.dates.filter(Boolean);
  const explicitTime = toTrimmedString(raw.holding_time);
  const time = isValidHoldingTime(explicitTime) && explicitTime ? explicitTime : parsed.time;
  const dateStart = toTrimmedString(raw.date_start) || dates[0] || '';
  const dateEnd = toTrimmedString(raw.date_end) || dates[dates.length - 1] || '';
  return {
    dates,
    time,
    dateStart,
    dateEnd,
  };
}

function photoUrlsForDraft(raw, config, warnings) {
  const fromConfig = normalizePhotoUrls(config.photoUrls || []);
  const rawList = Array.isArray(raw.photo_urls) ? raw.photo_urls : [];
  const fromRaw = normalizePhotoUrls(rawList);
  const source = fromConfig.length ? fromConfig : fromRaw;
  if (!fromConfig.length && rawList.length && !fromRaw.length) {
    warnings.push('ссылки photo_urls из файла не похожи на http(s) — добавьте фото вручную');
  }
  if (source.length > 15) warnings.push('ссылок на фото больше 15 — взяты первые 15');
  return source.slice(0, 15);
}

function buildDraft({
  raw, config, references, index,
}) {
  const warnings = [];
  const { categories, languages, cities } = references;

  let category = config.category || null;
  if (!category) {
    const rawId = toTrimmedString(raw.events_category_id || raw.category_id);
    category = (categories || []).find((item) => item._id === rawId) || null;
    if (rawId && !category) {
      warnings.push('категория из файла не найдена — выберите её вручную');
    }
  }

  let city = forcedCitySelection(config);
  let address = '';
  let coordinates = null;

  if (!city && shouldAskModelForEventCities(config)) {
    const rawCityId = toTrimmedString(raw.city_id);
    city = (cities || []).find((item) => item._id === rawCityId) || null;
    if (rawCityId && !city) {
      warnings.push('город из файла не найден — выберите его вручную');
    }
  }

  if (!isAllCitiesSelection(city)) {
    address = toTrimmedString(raw.address);
    coordinates = coordinatesFromRaw(raw);
  }

  const holding = holdingFromRaw(raw);
  if (!holding.dates.length) {
    warnings.push('нет дат проведения — добавьте хотя бы один день');
  }

  const rawLanguageIds = Array.isArray(raw.language_ids) ? raw.language_ids.map(toTrimmedString) : [];
  const eventLanguages = (languages || []).filter((lang) => rawLanguageIds.includes(lang._id));
  if (rawLanguageIds.length && !eventLanguages.length) {
    warnings.push('языки из файла не найдены в справочнике');
  }

  return {
    uid: makeUid(),
    sourceIndex: index,
    status: EVENT_STATUSES.draft,
    statusText: '',
    createdId: '',
    warnings,
    ownerId: toTrimmedString(config.ownerId),
    name: toTrimmedString(raw.name),
    category,
    holdingDates: holding.dates.length ? holding.dates : [''],
    holdingTime: holding.time,
    dateStart: holding.dateStart,
    dateEnd: holding.dateEnd,
    city,
    address,
    coordinates,
    contacts: contactsFromRaw(raw),
    description: toTrimmedString(raw.description),
    languages: eventLanguages,
    is_for_all_languages: eventLanguages.length === 0,
    photoUrls: photoUrlsForDraft(raw, config, warnings),
  };
}

function pickRawEvents(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return null;
  const keys = ['events', 'offers', 'items', 'list'];
  for (let i = 0; i < keys.length; i += 1) {
    if (Array.isArray(data[keys[i]])) return data[keys[i]];
  }
  return null;
}

export function parseBulkEventsJson(rawText, { config, references }) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(rawText));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  const rawEvents = pickRawEvents(data);
  if (!rawEvents) {
    return { error: 'Ожидается объект { "events": [...] } или массив мероприятий.' };
  }
  if (!rawEvents.length) {
    return { error: 'Массив events пуст — модель не прислала ни одного мероприятия.' };
  }

  const drafts = [];
  for (let i = 0; i < rawEvents.length; i += 1) {
    const raw = rawEvents[i];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return { error: `Мероприятие ${i + 1}: ожидался объект с полями.` };
    }
    drafts.push(buildDraft({
      raw, config, references, index: i,
    }));
  }
  return { drafts };
}

function draftToPayloadArgs(draft) {
  const isInAllCities = isAllCitiesSelection(draft.city);
  return {
    owner_id: draft.ownerId || '',
    name: draft.name || '',
    country_id: draft.city?.country_id,
    city_id: isInAllCities ? undefined : draft.city?._id,
    address: draft.address || '',
    coordinates: draft.coordinates,
    holding_dates: draft.holdingDates || [],
    holding_time: draft.holdingTime || '',
    date_start: draft.dateStart || '',
    date_end: draft.dateEnd || '',
    contacts: draft.contacts || {},
    description: draft.description || '',
    photos: (draft.photoUrls || []).map((url) => ({ url })),
    withPhotos: true,
    events_category_id: draft.category?._id,
    is_in_all_cities_of_country: isInAllCities,
    languages: draft.languages || [],
    is_for_all_languages: draft.is_for_all_languages !== undefined
      ? Boolean(draft.is_for_all_languages)
      : true,
  };
}

export function validateEventDraft(draft) {
  const { errors = [] } = checkAddEventPayload(draftToPayloadArgs(draft));
  return errors;
}

export function buildEventDraftFormData(draft) {
  const { formData } = checkAddEventPayload(draftToPayloadArgs(draft));
  return formData;
}
